import { useMemo, useEffect } from 'react'
import {
  Building2,
  Brain,
  Zap,
  Palette,
  CheckCircle2,
  Loader2,
  Circle,
  AlertCircle,
  ArrowRight,
} from 'lucide-react'
import type { AgentStepLabel } from '../hooks/useAgentRunState'
import { skillLabel } from '../lib/labels'

export interface AgentPersona {
  id: string
  name: string
  role: string
  icon: React.ElementType
  color: string
  bg: string
  border: string
  text: string
  artifacts: string[]
}

export const AGENT_PERSONAS: AgentPersona[] = [
  {
    id: 'archie',
    name: 'Archie',
    role: 'Arquiteto de Soluções',
    icon: Building2,
    color: '#8b5cf6',
    bg: 'bg-violet-50 dark:bg-violet-900/20',
    border: 'border-violet-200 dark:border-violet-800',
    text: 'text-violet-700 dark:text-violet-300',
    artifacts: ['SDD', 'ADRs', 'Detecção HITL'],
  },
  {
    id: 'dochie',
    name: 'Dochie',
    role: 'Analista Multidimensional',
    icon: Brain,
    color: '#10b981',
    bg: 'bg-emerald-50 dark:bg-emerald-900/20',
    border: 'border-emerald-200 dark:border-emerald-800',
    text: 'text-emerald-700 dark:text-emerald-300',
    artifacts: ['Análise Técnica', 'Riscos', 'Inovação', 'Sustentabilidade'],
  },
  {
    id: 'jobs',
    name: 'Jobs',
    role: 'Especialista em Event Storming',
    icon: Zap,
    color: '#f59e0b',
    bg: 'bg-amber-50 dark:bg-amber-900/20',
    border: 'border-amber-200 dark:border-amber-800',
    text: 'text-amber-700 dark:text-amber-300',
    artifacts: ['Domain Events', 'Commands', 'Aggregates', 'Policies'],
  },
  {
    id: 'vangogh',
    name: 'Van Gogh',
    role: 'Visualizador de Arquitetura',
    icon: Palette,
    color: '#f43f5e',
    bg: 'bg-rose-50 dark:bg-rose-900/20',
    border: 'border-rose-200 dark:border-rose-800',
    text: 'text-rose-700 dark:text-rose-300',
    artifacts: ['Diagramas Mermaid', 'Protótipo'],
  },
]

export type AgentStatus = 'idle' | 'working' | 'done' | 'error'

interface AgentTeamPanelProps {
  statuses: Record<string, AgentStatus> | { archie: AgentStatus; dochie: AgentStatus; jobs: AgentStatus; vangogh: AgentStatus }
  onAgentClick?: (agentId: string) => void
  onClose?: () => void
  artifactCounts?: Record<string, number>
  isOpen?: boolean
  stepLabels?: Record<string, AgentStepLabel>
}

const STATUS_CONFIG: Record<AgentStatus, { icon: React.ElementType; label: string; dot: string }> = {
  idle: { icon: Circle, label: 'Aguardando', dot: 'bg-gray-300 dark:bg-gray-600' },
  working: { icon: Loader2, label: 'Trabalhando', dot: 'bg-blue-500 animate-pulse' },
  done: { icon: CheckCircle2, label: 'Concluído', dot: 'bg-green-500' },
  error: { icon: AlertCircle, label: 'Erro', dot: 'bg-red-500' },
}

export default function AgentTeamPanel({ statuses, onAgentClick, onClose, artifactCounts = {}, isOpen, stepLabels = {} }: AgentTeamPanelProps) {
  const agents = useMemo(() => {
    const s = statuses as Record<string, AgentStatus>
    return AGENT_PERSONAS.map((a) => ({
      ...a,
      status: s[a.id] || 'idle',
      count: artifactCounts[a.id] || 0,
      stepLabel: stepLabels[a.id],
    }))
  }, [statuses, artifactCounts, stepLabels])

  // Fecha com ESC (diálogo modal)
  useEffect(() => {
    if (!isOpen || !onClose) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [isOpen, onClose])

  if (!isOpen) return null

  const activeCount = agents.filter((a) => a.status === 'working').length
  const doneCount = agents.filter((a) => a.status === 'done').length

  return (
    <div
      className="absolute inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose?.()
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Equipe de agentes"
        className="w-full max-w-4xl max-h-[85vh] bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-2xl flex flex-col overflow-hidden"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3">
            <div className="flex -space-x-2">
              {AGENT_PERSONAS.map((a) => (
                <div
                  key={a.id}
                  className="w-8 h-8 rounded-full flex items-center justify-center border-2 border-white dark:border-gray-900"
                  style={{ backgroundColor: a.color }}
                >
                  <a.icon size={14} className="text-white" />
                </div>
              ))}
            </div>
            <div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Equipe de Agentes</h3>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {activeCount > 0 ? (
                  <span className="flex items-center gap-1">
                    <Loader2 size={10} className="animate-spin text-blue-500" />
                    {activeCount} agente{activeCount > 1 ? 's' : ''} trabalhando
                  </span>
                ) : doneCount > 0 ? (
                  <span className="text-green-600 dark:text-green-400">
                    {doneCount} de {agents.length} agentes concluíram o trabalho
                  </span>
                ) : (
                  'Adicione ideias no Canvas para ativar os agentes'
                )}
              </p>
            </div>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-lg text-sm font-medium text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              Fechar
            </button>
          )}
        </div>

        {/* Agents Grid */}
        <div className="flex-1 overflow-auto p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {agents.map((agent) => {
              const statusCfg = STATUS_CONFIG[agent.status as AgentStatus]
              return (
                <button
                  key={agent.id}
                  type="button"
                  onClick={() => onAgentClick?.(agent.id)}
                  aria-label={`Abrir artefatos de ${agent.name} (${agent.role})`}
                  className={`relative group rounded-xl border p-5 cursor-pointer text-left w-full transition-all duration-200 hover:shadow-md focus:outline-none focus:ring-2 focus:ring-agent ${agent.bg} ${agent.border} ${
                    agent.status === 'working' ? 'ring-2 ring-blue-400/30' : ''
                  }`}
                >
                  {/* Status dot */}
                  <div className="absolute top-4 right-4 flex items-center gap-1.5">
                    <div className={`w-2 h-2 rounded-full ${statusCfg.dot}`} />
                    <span className={`text-[10px] font-medium ${agent.text} opacity-70`}>{statusCfg.label}</span>
                  </div>

                  <div className="flex items-start gap-4">
                    {/* Avatar */}
                    <div
                      className="w-12 h-12 rounded-xl flex items-center justify-center shrink-0 shadow-sm"
                      style={{ backgroundColor: agent.color }}
                    >
                      <agent.icon size={24} className="text-white" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className={`text-sm font-bold ${agent.text}`}>{agent.name}</h4>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{agent.role}</p>

                      {/* Step label — shows what this agent is actually doing */}
                      {agent.stepLabel && agent.stepLabel.label && (
                        <div className="mt-2">
                          <div className="flex items-center gap-1.5">
                            {agent.status === 'working' && <Loader2 size={10} className="animate-spin text-blue-500" />}
                            {agent.status === 'done' && <CheckCircle2 size={10} className="text-green-500" />}
                            {agent.status === 'idle' && <Circle size={10} className="text-gray-400" />}
                            {agent.status === 'error' && <AlertCircle size={10} className="text-red-500" />}
                            <span className={`text-[11px] font-medium ${
                              agent.status === 'working' ? 'text-blue-600 dark:text-blue-400' :
                              agent.status === 'done' ? 'text-green-600 dark:text-green-400' :
                              agent.status === 'error' ? 'text-red-600 dark:text-red-400' :
                              'text-gray-500 dark:text-gray-400'
                            }`}>
                              {agent.stepLabel.label}
                            </span>
                          </div>
                          {agent.stepLabel.activeSkills && agent.stepLabel.activeSkills.length > 0 && (
                            <div className="mt-1 flex flex-wrap gap-1">
                              {agent.stepLabel.activeSkills.map((sid) => (
                                <span
                                  key={sid}
                                  className="text-[9px] px-1.5 py-0.5 rounded-full font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300"
                                >
                                  {skillLabel(sid)}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      )}

                      {/* Artifacts */}
                      <div className="mt-3 flex flex-wrap gap-1.5">
                        {agent.artifacts.map((art) => (
                          <span
                            key={art}
                            className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-white/60 dark:bg-black/20 text-gray-600 dark:text-gray-300 border border-gray-200/50 dark:border-gray-700/50"
                          >
                            {art}
                          </span>
                        ))}
                      </div>

                      {/* Count / CTA */}
                      {agent.count > 0 && (
                        <div className="mt-3 flex items-center gap-1.5 text-xs">
                          <CheckCircle2 size={12} className="text-green-500" />
                          <span className="text-gray-600 dark:text-gray-300">{agent.count} artefato{agent.count > 1 ? 's' : ''}</span>
                          <ArrowRight size={12} className="text-gray-400 group-hover:translate-x-0.5 transition-transform" />
                        </div>
                      )}

                      {agent.status === 'working' && !agent.stepLabel && (
                        <div className="mt-3 flex items-center gap-2 text-xs text-blue-600 dark:text-blue-400">
                          <Loader2 size={12} className="animate-spin" />
                          <span>Gerando artefatos...</span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Footer hint */}
        <div className="px-6 py-3 border-t border-gray-200 dark:border-gray-800 bg-gray-50/50 dark:bg-gray-800/50">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Clique em um agente para abrir o artefato que ele produz — Archie e Dochie: SDD · Jobs: Event Storm · Van Gogh: diagramas e protótipo
          </p>
        </div>
      </div>
    </div>
  )
}

// Mini footer bar component for showing agent statuses compactly
export function AgentTeamBar({
  statuses,
  onAgentClick,
  stepLabels = {},
}: {
  statuses: Record<string, AgentStatus> | { archie: AgentStatus; dochie: AgentStatus; jobs: AgentStatus; vangogh: AgentStatus }
  onAgentClick?: (agentId: string) => void
  stepLabels?: Record<string, AgentStepLabel>
}) {
  return (
    <div className="flex items-center gap-1">
      {AGENT_PERSONAS.map((agent) => {
        const s = statuses as Record<string, AgentStatus>
        const status = s[agent.id] || 'idle'
        const isWorking = status === 'working'
        const label = stepLabels[agent.id]
        return (
          <button
            key={agent.id}
            onClick={() => onAgentClick?.(agent.id)}
            className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-medium transition-all ${
              isWorking
                ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 ring-1 ring-blue-200 dark:ring-blue-800'
                : status === 'done'
                ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300'
                : 'text-gray-500 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800'
            }`}
            title={label?.label || agent.role}
          >
            <agent.icon size={12} style={{ color: agent.color }} />
            <span className="hidden sm:inline">{agent.name}</span>
            {isWorking && <Loader2 size={10} className="animate-spin" />}
            {status === 'done' && <CheckCircle2 size={10} />}
          </button>
        )
      })}
    </div>
  )
}
